// Dono: B3. Barra da seleção em lote (etiquetar, pôr numa coleção, apagar com um desfazer). Montada em Tudo.
// Entra pelo botão "Selecionar", por Shift/Ctrl/⌘ + clique num card ou por toque longo; Esc sai.
import { useEffect, useId, useRef, useState } from "preact/hooks";
import type { Space } from "../../../../shared/types.ts";
import type { BatchAction } from "../../data/types.ts";
import { IconRing, IconTrash } from "../Icons.tsx";
import { failed, store, toast, topTags, visible } from "../state.ts";
import { SpaceChooser } from "./AddToSpace.tsx";
import "../../styles/organize.css";
import { IconSelect, IconTag } from "./icons.tsx";
import { cardsLabel } from "./order.ts";
import { selectAll, selected, selecting, stopSelecting } from "./selection.ts";

type Panel = "tag" | "space" | null;

async function apply(ids: string[], action: BatchAction, done: string) {
  try {
    const undo = await store.applyMany(ids, action);
    stopSelecting();
    toast({ key: `batch:${Date.now()}`, text: done, action: { label: "Desfazer", run: () => void undo().catch(failed) } }, 8000);
  } catch {
    failed();
  }
}

export function BatchBar() {
  useEffect(() => stopSelecting, []); // sair de Tudo encerra a seleção
  if (selecting.value) return <Bar />;
  if (!visible.value.length) return null; // nada para selecionar (conta vazia, busca sem resultado)
  // No celular (≤ 768) a linha some: segurar um card seleciona, e o menu tem "Selecionar cards".
  return (
    <div class="select-row">
      <button type="button" class="select-toggle" aria-label="Selecionar cards" onClick={() => (selecting.value = true)}>
        <IconSelect />
        <span aria-hidden="true">Selecionar</span>
      </button>
    </div>
  );
}

function Bar() {
  const id = useId();
  const [panel, setPanel] = useState<Panel>(null);
  const bar = useRef<HTMLElement>(null);
  const ids = [...selected.value];
  const n = ids.length;
  const live = visible.value.map((c) => c.id);
  // A barra é a primeira coisa no foco: ao entrar, Tab chega nela antes dos cards.
  useEffect(() => bar.current?.querySelector<HTMLElement>(".batch-toggle-all")?.focus(), []);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || e.defaultPrevented || document.querySelector("dialog[open]")) return;
      e.preventDefault();
      if (panel) setPanel(null);
      else stopSelecting();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [panel]);
  useEffect(() => void (panel === "tag" && bar.current?.querySelector<HTMLInputElement>("input[name=tag]")?.focus()), [panel]);
  const toggle = (p: Panel) => setPanel(panel === p ? null : p);
  const none = n === 0;
  const all = n > 0 && live.every((x) => selected.value.has(x));
  const toSpace = (s: Space) => void apply(ids, { kind: "space", spaceId: s.id }, `${cardsLabel(n)} em “${s.name}”.`);

  return (
    <section class="batch" aria-label="Seleção em lote" ref={bar}>
      <div class="batch-inner">
        <p class="batch-count" aria-live="polite">
          <span>{n}</span> {n === 1 ? "card selecionado" : "cards selecionados"}
        </p>
        <div class="batch-actions">
          <div class="batch-slot">
            <button type="button" class="batch-act" aria-expanded={panel === "tag"} aria-controls={panel === "tag" ? `${id}-tag` : undefined} aria-disabled={none || undefined} onClick={() => !none && toggle("tag")}>
              <IconTag />
              <span class="batch-label">Etiquetar</span>
            </button>
            {panel === "tag" && (
              <form
                id={`${id}-tag`}
                class="batch-pop"
                onSubmit={(e) => {
                  e.preventDefault();
                  const name = (e.currentTarget.elements.namedItem("tag") as HTMLInputElement).value.replace(/^#+/, "").trim();
                  if (name) void apply(ids, { kind: "tag", name }, `Etiqueta “${name}” em ${cardsLabel(n)}.`);
                }}
              >
                <input
                  name="tag"
                  class="chooser-find"
                  list={`${id}-tags`}
                  aria-label="Etiqueta para os cards selecionados"
                  placeholder="Etiqueta…"
                  autocomplete="off"
                  autocapitalize="none"
                  autocorrect="off"
                  spellcheck={false}
                  enterkeyhint="done"
                  maxLength={80}
                />
                <datalist id={`${id}-tags`}>
                  {topTags.value.slice(0, 30).map((t) => (
                    <option key={t.id} value={t.name} />
                  ))}
                </datalist>
                <button class="org-btn small">Etiquetar</button>
              </form>
            )}
          </div>
          <div class="batch-slot">
            <button type="button" class="batch-act" aria-expanded={panel === "space"} aria-controls={panel === "space" ? `${id}-space` : undefined} aria-disabled={none || undefined} onClick={() => !none && toggle("space")}>
              <IconRing />
              <span class="batch-label">Pôr numa coleção</span>
            </button>
            {panel === "space" && (
              <div id={`${id}-space`} class="batch-pop">
                <SpaceChooser manualOnly isIn={() => false} onPick={toSpace} onEscape={() => setPanel(null)} />
              </div>
            )}
          </div>
          <button
            type="button"
            class="batch-del"
            aria-label={none ? "Apagar os selecionados" : `Apagar ${cardsLabel(n)}`}
            aria-disabled={none || undefined}
            onClick={() => !none && void apply(ids, { kind: "delete" }, n === 1 ? "1 card movido para a lixeira." : `${n} cards movidos para a lixeira.`)}
          >
            <IconTrash />
            <span class="batch-label batch-del-label" aria-hidden="true">
              Apagar
            </span>
          </button>
        </div>
        <div class="batch-end">
          {/* No celular a ação fica curta ("Todos"); o nome acessível é o mesmo. */}
          <button type="button" class="batch-text batch-toggle-all" aria-label={all ? "Limpar" : "Selecionar todos"} onClick={() => (all ? selectAll([]) : selectAll(live))}>
            {all ? (
              "Limpar"
            ) : (
              <>
                <span class="batch-long">Selecionar </span>todos
              </>
            )}
          </button>
          <button type="button" class="batch-text batch-cancel" onClick={stopSelecting}>
            Cancelar
          </button>
        </div>
      </div>
    </section>
  );
}
